import React, { useState, useEffect, useRef } from 'react';
import {
  adminGetClients, adminAddClient, adminUpdateClient, adminDeleteClient,
  adminGetClientImages, adminUploadClientImages, adminUpdateClientImage, adminDeleteClientImage,
} from '../../api/admin';
import AdminLayout from './AdminLayout';
import './AdminClients.css';

// Font Awesome Icons
import { FaBuilding, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaImage, FaImages } from 'react-icons/fa';

const EMPTY = { id: null, name: '', description: '', website_url: '', sort_order: 0, is_active: 1 };

function AdminClients() {
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY);
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const fileRef                 = useRef();

  // Image gallery state
  const [galleryClient, setGalleryClient] = useState(null); // client currently being managed
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryFileRef = useRef();

  const openGallery = (client) => {
    setGalleryClient(client);
    loadGallery(client.id);
  };

  const closeGallery = () => {
    setGalleryClient(null);
    setGalleryImages([]);
  };

  const loadGallery = (clientId) => {
    setGalleryLoading(true);
    adminGetClientImages(clientId)
      .then((res) => { if (res.success) setGalleryImages(res.data || []); })
      .finally(() => setGalleryLoading(false));
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setGalleryUploading(true);
    try {
      await adminUploadClientImages(galleryClient.id, files, files.map(() => ''));
      loadGallery(galleryClient.id);
    } finally {
      setGalleryUploading(false);
      e.target.value = ''; // allow re-selecting the same file again later
    }
  };

  const handleGalleryCaptionChange = (img, caption) => {
    setGalleryImages((prev) => prev.map((i) => (i.id === img.id ? { ...i, caption } : i)));
  };

  const handleGalleryCaptionSave = async (img) => {
    await adminUpdateClientImage(img.id, img.caption || '', img.sort_order || 0);
  };

  const handleGalleryDelete = async (img) => {
    if (!window.confirm('Delete this image?')) return;
    await adminDeleteClientImage(img.id);
    loadGallery(galleryClient.id);
  };

  const moveGalleryImage = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= galleryImages.length) return;
    const reordered = [...galleryImages];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    setGalleryImages(reordered);
    // Persist new sort_order for both swapped images
    await Promise.all(reordered.map((img, i) => adminUpdateClientImage(img.id, img.caption || '', i)));
    loadGallery(galleryClient.id);
  };

  const load = () => {
    setLoading(true);
    adminGetClients()
      .then(r => { if (r.success) setClients(r.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setForm(EMPTY); setLogoFile(null); setPreview(''); setShowForm(true); setMsg(''); };
  const openEdit = (c) => { setForm({ ...c }); setLogoFile(null); setPreview(c.logo_url || ''); setShowForm(true); setMsg(''); };
  const close    = () => { setShowForm(false); setMsg(''); };

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('Client name is required'); return; }
    setSaving(true); setMsg('');
    try {
      let res;
      if (form.id) {
        res = await adminUpdateClient({ ...form });
      } else {
        const fd = new FormData();
        fd.append('name',        form.name);
        fd.append('description', form.description || '');
        fd.append('website_url', form.website_url);
        fd.append('sort_order',  form.sort_order);
        fd.append('is_active',   form.is_active);
        if (logoFile) fd.append('logo', logoFile);
        res = await adminAddClient(fd);
      }
      if (res.success) { setMsg('Saved successfully!'); load(); setTimeout(close, 800); }
      else setMsg('Error: ' + res.message);
    } catch { setMsg('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await adminDeleteClient(id);
    load();
  };

  const toggleActive = async (client) => {
    await adminUpdateClient({ ...client, is_active: client.is_active ? 0 : 1 });
    load();
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <AdminLayout title="Clients">
      <div className="adm-page-header">
        <h2>Manage Clients</h2>
        <button className="adm-btn-primary" onClick={openAdd}>
          <FaPlus /> Add Client
        </button>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="adm-modal-overlay" onClick={close}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{form.id ? 'Edit Client' : 'Add New Client'}</h3>
              <button onClick={close}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.startsWith('Saved') ? 'adm-msg--success' : 'adm-msg--error'}`}>{msg}</div>}

              <div className="adm-form-field">
                <label>Client / Company Name *</label>
                <input value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Tata Projects" />
              </div>

              <div className="adm-form-field">
                <label>Website URL</label>
                <input value={form.website_url} onChange={e => f('website_url', e.target.value)} placeholder="https://example.com" />
              </div>

              <div className="adm-form-field">
                <label>Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={e => f('description', e.target.value)}
                  placeholder="A short description of this client / partnership (shown on the website)"
                  rows={4}
                  className="adm-textarea"
                />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => f('sort_order', e.target.value)} />
                </div>
                <div className="adm-form-field">
                  <label>Status</label>
                  <select value={form.is_active} onChange={e => f('is_active', parseInt(e.target.value))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="adm-form-field">
                <label>Logo {form.id ? '(upload to replace)' : ''}</label>
                <div className="adm-upload-box" onClick={() => fileRef.current.click()}>
                  {preview
                    ? <img src={preview} alt="logo preview" className="adm-upload-preview" />
                    : <div className="adm-upload-placeholder">
                        <FaImage /> Click to upload logo
                      </div>
                  }
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) { setLogoFile(file); setPreview(URL.createObjectURL(file)); }
                  }}
                />
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={close}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      {galleryClient && (
        <div className="adm-modal-overlay" onClick={closeGallery}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>Image Gallery — {galleryClient.name}</h3>
              <button onClick={closeGallery}>✕</button>
            </div>
            <div className="adm-modal__body">
              <div className="adm-form-field">
                <button
                  className="adm-btn-primary"
                  onClick={() => galleryFileRef.current.click()}
                  disabled={galleryUploading}
                >
                  <FaPlus /> {galleryUploading ? 'Uploading…' : 'Add Images'}
                </button>
                <input
                  ref={galleryFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleGalleryUpload}
                />
              </div>

              {galleryLoading ? (
                <div className="adm-loading">Loading…</div>
              ) : galleryImages.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888' }}>No gallery images yet. Add some above.</p>
              ) : (
                <div className="adm-gallery-grid">
                  {galleryImages.map((img, index) => (
                    <div key={img.id} className="adm-gallery-item">
                      <img src={img.image_url} alt={img.caption || galleryClient.name} className="adm-gallery-thumb" />
                      <input
                        type="text"
                        className="adm-gallery-caption"
                        placeholder="Caption (optional)"
                        value={img.caption || ''}
                        onChange={(e) => handleGalleryCaptionChange(img, e.target.value)}
                        onBlur={() => handleGalleryCaptionSave(img)}
                      />
                      <div className="adm-gallery-item__actions">
                        <button onClick={() => moveGalleryImage(index, -1)} disabled={index === 0} title="Move left">←</button>
                        <button onClick={() => moveGalleryImage(index, 1)} disabled={index === galleryImages.length - 1} title="Move right">→</button>
                        <button onClick={() => handleGalleryDelete(img)} className="adm-gallery-item__delete" title="Delete">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={closeGallery}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading…</div> : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Name</th>
                  <th>Website</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No clients added yet.</td></tr>
                )}
                {clients.map(c => (
                  <tr key={c.id}>
                    <td>
                      {c.logo_url
                        ? <img src={c.logo_url} alt={c.name} className="adm-thumb" style={{ objectFit: 'contain', background: '#fff' }} />
                        : <span className="adm-no-img" style={{ fontSize: 22 }}>
                            <FaBuilding style={{ color: '#6c757d' }} />
                          </span>
                      }
                    </td>
                    <td><strong>{c.name}</strong></td>
                    <td>
                      {c.website_url
                        ? <a href={c.website_url} target="_blank" rel="noreferrer" style={{ color: '#00CCCD' }}>{c.website_url}</a>
                        : '—'
                      }
                    </td>
                    <td>{c.sort_order}</td>
                    <td>
                      <button
                        className={`adm-toggle ${c.is_active ? 'adm-toggle--on' : 'adm-toggle--off'}`}
                        onClick={() => toggleActive(c)}
                      >
                        {c.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        {c.is_active ? ' Active' : ' Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn-edit" onClick={() => openEdit(c)}>
                          <FaEdit /> Edit
                        </button>
                        <button className="adm-btn-edit" onClick={() => openGallery(c)} title="Manage image gallery">
                          <FaImages /> Gallery
                        </button>
                        <button className="adm-btn-delete" onClick={() => handleDelete(c.id, c.name)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminClients;